import { useState, useRef, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Card,
  Row,
  Col,
  Divider,
  Typography,
  Space,
  Radio,
  Select,
} from "antd";
import { useNavigate } from "react-router-dom";
import { createUser, getUserByUsername } from "../../../../services/users";
import {
  UserDesignationSelector,
  UserRoleSelector,
} from "../../../../components/selector";
import useGoBackOrDefault from "../../../../hooks/useDefaultNavigation";
import {
  MailOutlined,
  UserOutlined,
  LockOutlined,
  IdcardOutlined,
  SolutionOutlined,
  MedicineBoxOutlined,
  ClusterOutlined,
} from "@ant-design/icons";
import { useNotification } from "../../../../components/notification.provider";
import {
  fetchDistricts,
  fetchHospitalsByDistrictId,
} from "../../../../services/districts";

const { Title } = Typography;

const UserAddPage = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const usernameCheckTimeout = useRef();
  const [usernameValidating, setUsernameValidating] = useState(false);

  const isWatchedOtherDesignation = Form.useWatch("designation", form);
  const isWatchedDistrict = Form.useWatch("districtUid", form);

  const goBackToIndexOrBack = useGoBackOrDefault({
    defaultUrl: "/manage/auth/users",
  });

  const notification = useNotification();
  const navigate = useNavigate();

  // Async validator for username
  const validateUsername = async (_, value) => {
    if (!value) return Promise.reject("Please input username");
    if (!/^[a-zA-Z0-9._-]+$/.test(value)) {
      return Promise.reject(
        "Username can only contain letters, numbers, dots, underscores, and hyphens"
      );
    }
    // Debounce API call
    if (usernameCheckTimeout.current)
      clearTimeout(usernameCheckTimeout.current);
    setUsernameValidating(true);
    return new Promise((resolve, reject) => {
      usernameCheckTimeout.current = setTimeout(async () => {
        try {
          const user = await getUserByUsername(value);
          setUsernameValidating(false);
          if (user) {
            reject("Username already exists. Please choose another.");
          } else {
            resolve();
          }
        } catch (err) {
          setUsernameValidating(false);
          resolve(); // If API fails, don't block user
        }
      }, 700); // 700ms debounce
    });
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);
      const userData = {
        ...values,
        enabled: true,
        avatarUrl: avatarPreview || null,
      };

      const newUser = await createUser(userData);

      // await sendUserInviteEmail(newUser.email);
      notification.success({
        message: "User created",
        description: "User created and invitation email sent!",
      });

      navigate(-1);
    } catch (err) {
      notification.error({
        message: "Creation failed",
        description: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const loadDistricts = async () => {
    try {
      // setLoading(true);
      const data = await fetchDistricts();
      setDistricts(data);
    } catch (err) {
      notification.error({
        message: "Failed to load districts data",
        description: err.message,
      });
      console.error("Loading failed", err);
    } finally {
      setLoading(false);
    }
  };

  const loadHospitalsByDistrictId = async (districtId) => {
    try {
      // setLoading(true);
      const data = await fetchHospitalsByDistrictId(districtId);
      setHospitals(data);
    } catch (err) {
      notification.error({
        message: "Failed to load hospitals data",
        description: err.message,
      });
      console.error("Loading failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDistricts();
  }, [form]);

  useEffect(() => {
    if (isWatchedDistrict) {
      const currentDistrict = form.getFieldValue("districtUid");
      if (currentDistrict) {
        loadHospitalsByDistrictId(currentDistrict);
      }
    }
  }, [isWatchedDistrict, form]);

  return (
    <Card
      variant={false}
      style={{
        width: "100%",
        margin: "24px auto",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        borderRadius: 8,
        padding: "0 32px",
        minWidth: 320,
        maxWidth: "1200px",
      }}
      title={
        <Title level={4} style={{ margin: 0 }}>
          <UserOutlined style={{ marginRight: 8 }} />
          Create New User
        </Title>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        style={{ padding: "0 16px" }}
      >
        <Row gutter={24}>
          {/* Avatar Upload */}
          {/* <Col span={24} style={{ textAlign: 'center', marginBottom: 24 }}>
            <label htmlFor="avatar-upload">
              <Avatar 
                size={100} 
                src={avatarPreview} 
                icon={<UserOutlined />}
                style={{ 
                  cursor: 'pointer',
                  border: '2px dashed #d9d9d9',
                  backgroundColor: '#fafafa'
                }}
              />
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                style={{ display: 'none' }}
              />
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">Click to upload profile photo</Text>
              </div>
            </label>
          </Col> */}

          {/* Basic Info */}
          <Col xs={24} md={12}>
            <Form.Item
              label="First Name"
              name="firstName"
              validateDebounce={2000}
              rules={[{ required: true, message: "Please input first name" }]}
            >
              <Input prefix={<UserOutlined />} placeholder="John" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Last Name"
              name="lastName"
              validateDebounce={1000}
              rules={[{ required: true, message: "Please input last name" }]}
            >
              <Input prefix={<UserOutlined />} placeholder="Doe" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Username"
              name="username"
              validateFirst
              hasFeedback
              validateStatus={usernameValidating ? "validating" : undefined}
              rules={[
                { required: true, message: "Please input username" },
                { validator: validateUsername },
              ]}
            >
              <Input prefix={<IdcardOutlined />} placeholder="johndoe" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Email"
              name="email"
              validateDebounce={1000}
              rules={[
                {
                  required: true,
                  message: "Please enter user's email address",
                },
                { type: "email", message: "Please enter valid email" },
              ]}
            >
              <Input prefix={<MailOutlined />} placeholder="john@example.com" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Password"
              name="password"
              validateDebounce={1000}
              rules={[
                { required: true, message: "Please input password" },
                { min: 8, message: "Password must be at least 8 characters" },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="••••••••"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Confirm Password"
              name="confirmPassword"
              validateDebounce={1000}
              dependencies={["password"]}
              rules={[
                { required: true, message: "Please confirm password" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject("The two passwords do not match!");
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="••••••••"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Gender"
              name="gender"
              validateFirst
              hasFeedback
              rules={[{ required: true, message: "Please select gender" }]}
            >
              <Radio.Group>
                <Radio value="MALE">Male</Radio>
                <Radio value="FEMALE">Female</Radio>
                {/* <Radio value="OTHER">Other</Radio> */}
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="District"
              name="districtUid"
              validateFirst
              hasFeedback
              rules={[{ required: true, message: "Please select district" }]}
            >
              <Select
                placeholder="Select district..."
                loading={false}
                prefix={<ClusterOutlined />}
              >
                {districts?.map((district) => (
                  <Select.Option key={district.uid} value={district.uid}>
                    {district.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          {isWatchedDistrict && (
            <Col xs={24} md={12}>
              <Form.Item
                label="Hospital"
                name="hospitalUid"
                validateFirst
                hasFeedback
                rules={[{ required: true, message: "Please select hospital" }]}
              >
                <Select
                  placeholder="Select hospital..."
                  loading={loading}
                  prefix={<MedicineBoxOutlined />}
                >
                  {hospitals?.map((hospital) => (
                    <Select.Option key={hospital?.uid} value={hospital?.uid}>
                      {hospital?.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}

          {/* Role and Designation */}
          <Col span={24}>
            <Divider orientation="left" style={{ color: "#666" }}>
              User Designation & Privileges
            </Divider>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Designation"
              name="designation"
              tooltip="User's professional title or position"
              rules={[
                {
                  required: true,
                  message:
                    "Please select user's designation. Specify OTHER if designation does not exist",
                },
              ]}
            >
              <UserDesignationSelector />
            </Form.Item>
            {isWatchedOtherDesignation == "OTHER" && (
              <Form.Item
                label="Custom Designation"
                name="customDesignation"
                tooltip="User's specific designaton"
              >
                <Input
                  prefix={<SolutionOutlined />}
                  placeholder="Please specify designation"
                />
              </Form.Item>
            )}
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Roles"
              name="roles"
              rules={[
                { required: true, message: "Please select just one role role" },
              ]}
              tooltip="Determines system access permissions"
            >
              <UserRoleSelector />
            </Form.Item>
          </Col>

          {/* Notification Settings */}
          {/* <Col span={24}>
            <Divider orientation="left" style={{ color: "#666" }}>
              Notification Settings
            </Divider>
            <Form.Item>
              <Space align="baseline">
                <Switch
                  checked={sendInvite}
                  onChange={setSendInvite}
                  checkedChildren={<MailOutlined />}
                  unCheckedChildren={<MailOutlined />}
                />
                <Text>
                  {sendInvite
                    ? "Send welcome email with account activation link"
                    : "Do not send welcome email (manual activation required)"}
                </Text>
              </Space>
            </Form.Item>
          </Col> */}

          {/* Form Actions */}
          <Col span={24} style={{ marginTop: 24 }}>
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="large"
                >
                  Create User
                </Button>
                <Button onClick={goBackToIndexOrBack} size="large">
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default UserAddPage;
